import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@headerhawk/ui";
import { SiteForm } from "@/components/onboarding/site-form";
import { api, type Site } from "@/lib/api/client";
import { toast } from "sonner";
import { hostRejectionsOf, type SiteFormData } from "@/lib/validation/site";
import { noteTenantHasHadSite } from "@/lib/sites/first-site";

interface AddSiteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** With the site just created, whose setup is the next thing to show. */
  onSuccess: (site: Site) => void;
}

export function AddSiteDialog({
  open,
  onOpenChange,
  onSuccess,
}: AddSiteDialogProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const queryClient = useQueryClient();

  const handleSubmit = async (data: SiteFormData) => {
    setIsSubmitting(true);
    try {
      const { site } = await api.sites.create(data);
      await noteTenantHasHadSite(queryClient, site.createdAt);
      toast.success("Site created successfully");
      onSuccess(site);
    } catch (error) {
      // A refused host is shown beside the host field by the form itself.
      if (hostRejectionsOf(error)) throw error;
      const message =
        error instanceof Error ? error.message : "Failed to create site";
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add site</DialogTitle>
          <DialogDescription>
            Add a new website to monitor for CSP violations.
          </DialogDescription>
        </DialogHeader>
        <SiteForm
          onSubmit={handleSubmit}
          isSubmitting={isSubmitting}
          submitLabel="Add site"
        />
      </DialogContent>
    </Dialog>
  );
}
